import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AddIcon,
  CopyIcon,
  DeleteIcon,
  ExpandMoreIcon,
  FileCopyIcon,
  LockIcon,
  PlayArrowIcon,
  RefreshIcon,
  SearchIcon,
  UploadFileIcon,
} from 'assets/icons';
import config from 'config';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAccessToken } from 'redux/slices/user.slice';
import { refreshAccessToken } from 'services/api/baseApi';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RouteInfo {
  route: string;
  method: Method;
  example?: unknown;
}

interface RawResult {
  status: number;
  body: string;
  time: number;
}

interface RequestResult extends RawResult {
  ok: boolean;
  hasFile: boolean;
}

interface ApiRequest {
  id: number;
  url: string;
  method: Method;
  body: string;
  label?: string;
  result?: RequestResult;
  logLink?: string;
}

const METHODS: Method[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const QUICK_TAGS = [
  { label: 'admin', value: '/admin/' },
  { label: ':userId', value: ':userId' },
  { label: ':businessUnitId', value: ':businessUnitId' },
  { label: ':clientId', value: ':clientId' },
  { label: ':carId', value: ':carId' },
];

// Detects unreplaced path params like :id or :user-name.
// Ignores colons in query string values (e.g. ?filter=name:lastName).
const hasUnreplacedParams = (url: string): boolean => {
  const [path] = url.split('?');
  return /:[a-zA-Z][a-zA-Z0-9\-_]*/.test(path);
};

const isJsonValid = (s: string): boolean => {
  try {
    JSON.parse(s);
    return true;
  } catch {
    return false;
  }
};

let idCounter = 0;
const nextId = () => ++idCounter;

const makeEmptyRequest = (): ApiRequest => ({ id: nextId(), url: '', method: 'POST', body: '{}' });

const buildLogLink = (requestId: string): string => {
  let serviceName = '';
  let projectNumber = '';
  try {
    const hostname = new URL(config.apiUrl).hostname;
    const slug = hostname.split('.')[0]; // e.g. "relif-saas-back-workload-816446680429"
    const match = slug.match(/^(.+)-(\d+)$/);
    if (match) {
      [, serviceName, projectNumber] = match;
    }
  } catch {
    /* use empty fallbacks */
  }
  const filter = [
    `resource.type="cloud_run_revision"`,
    `resource.labels.service_name="${serviceName}"`,
    `"[r_${requestId}]"`,
  ].join('\n');
  return `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(filter)};duration=PT15M?project=${projectNumber}`;
};

const isDefaultRequest = (req: ApiRequest) => req.url === '' && req.body === '{}';

const parseRequestsParam = (): ApiRequest[] => {
  try {
    const raw = new URLSearchParams(window.location.search).get('requests');
    if (!raw) return [makeEmptyRequest()];
    const parsed = JSON.parse(raw) as Array<{ url?: string; method?: string; body?: unknown; label?: string }>;
    return parsed.map((r) => ({
      id: nextId(),
      url: r.url ?? '',
      method: (r.method as Method) ?? 'POST',
      body: r.body !== undefined ? JSON.stringify(r.body, null, 2) : '{}',
      label: r.label,
    }));
  } catch {
    return [makeEmptyRequest()];
  }
};

const METHOD_COLORS: Record<Method, string> = {
  GET: '#2e7d32',
  POST: '#1565c0',
  PUT: '#e65100',
  PATCH: '#6a1b9a',
  DELETE: '#c62828',
};

const MethodBadge = ({ method }: { method: Method }) => (
  <Typography variant="caption" sx={{ color: METHOD_COLORS[method], minWidth: 48, letterSpacing: '0.05em' }}>
    {method}
  </Typography>
);

const CopyIconButton = ({ text, title }: { text: string; title: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? '¡Copiado!' : title}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          void navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        sx={{ color: copied ? '#2e7d32' : 'text.disabled', '&:hover': { color: NAVY } }}
      >
        <CopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
};

const PreBlock = ({ children }: { children: string }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.5,
      bgcolor: alpha(NAVY, 0.04),
      borderRadius: RADIUS_ITEM,
      fontSize: 12,
      fontFamily: 'monospace',
      overflowX: 'auto',
      maxHeight: 300,
      overflowY: 'auto',
      color: NAVY,
    }}
  >
    {children}
  </Box>
);

const ResponseBody = ({ result }: { result: RequestResult }) => (
  <Accordion disableGutters elevation={0} defaultExpanded sx={{ bgcolor: 'transparent' }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 36 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" color={NAVY}>
          Respuesta {result.status}
        </Typography>
        <CopyIconButton text={result.body} title="Copiar respuesta" />
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0, pt: 0 }}>
      {!result.ok ? (
        <Alert severity="error" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
          {result.body}
        </Alert>
      ) : (
        <PreBlock>{result.body}</PreBlock>
      )}
    </AccordionDetails>
  </Accordion>
);

const UrlsForm = () => {
  const accessToken = useSelector((state: { user?: { accessToken?: string } }) => state.user?.accessToken ?? '');

  const [requests, setRequests] = useState<ApiRequest[]>(parseRequestsParam);
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());
  const [files, setFiles] = useState<Map<number, File>>(new Map());
  const [tokenOverride, setTokenOverride] = useState('');
  const [runProgress, setRunProgress] = useState<{ done: number; total: number } | null>(null);

  const [allRoutes, setAllRoutes] = useState<RouteInfo[]>([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routeSearch, setRouteSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState<Method | ''>('');
  const [tagFilter, setTagFilter] = useState('');
  const [copied, setCopied] = useState(false);

  const fileInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());

  const dispatch = useDispatch();

  const authHeaders = useCallback(
    (token: string, extra?: Record<string, string>) => ({
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...extra,
    }),
    []
  );

  // On 401, refresh the token once (shared with baseApi) and retry, instead of forcing a reload.
  const authFetch = useCallback(
    async (url: string, init: RequestInit = {}, extraHeaders?: Record<string, string>) => {
      const token = tokenOverride || accessToken;
      const res = await fetch(url, { ...init, headers: authHeaders(token, extraHeaders) });
      if (res.status !== 401 || tokenOverride) return res;
      const newToken = await refreshAccessToken(token);
      if (!newToken) return res;
      dispatch(setAccessToken(newToken));
      return fetch(url, { ...init, headers: authHeaders(newToken, extraHeaders) });
    },
    [accessToken, tokenOverride, authHeaders, dispatch]
  );

  const fetchRoutes = useCallback(() => {
    setRoutesLoading(true);
    fetch(`${config.apiUrl}/getAllRoutes`, { headers: authHeaders(tokenOverride || accessToken) })
      .then((r) => r.json())
      .then((routes: RouteInfo[]) => setAllRoutes(routes))
      .catch(() => {})
      .finally(() => setRoutesLoading(false));
  }, [authHeaders, accessToken, tokenOverride]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const executeRequest = async (req: ApiRequest, file?: File, requestId?: string): Promise<RawResult> => {
    const start = performance.now();
    const url = `${config.workloadApiUrl}/${req.url.replace(/^\//, '')}`;
    const isGet = req.method === 'GET';

    let reqBody: BodyInit | undefined;
    let contentType: Record<string, string> = {};

    if (!isGet) {
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          const parsed = JSON.parse(req.body) as Record<string, unknown>;
          for (const key of Object.keys(parsed)) formData.append(key, String(parsed[key]));
        } catch {
          /* skip body merge */
        }
        reqBody = formData;
      } else {
        reqBody = req.body;
        contentType = { 'Content-Type': 'application/json' };
      }
    }

    const res = await authFetch(
      url,
      { method: req.method, body: reqBody },
      { ...contentType, ...(requestId ? { 'X-Request-Id': requestId } : {}) }
    );
    const time = Math.round(performance.now() - start);
    const text = await res.text();
    let pretty = text;
    try {
      pretty = JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      /* keep raw */
    }
    return { status: res.status, body: pretty, time };
  };

  const runRequest = async (req: ApiRequest) => {
    const requestId = Math.random().toString(36).substring(2, 9);
    const logLink = buildLogLink(requestId);
    setLoadingIds((prev) => new Set(prev).add(req.id));
    setRequests((prev) => prev.map((r) => (r.id === req.id ? { ...r, logLink } : r)));
    const file = files.get(req.id);
    const start = performance.now();
    try {
      const result = await executeRequest(req, file, requestId);
      const ok = result.status < 400;
      const log = {
        request: { method: req.method, url: req.url, body: req.body },
        response: {
          status: result.status,
          body: (() => {
            try {
              return JSON.parse(result.body) as unknown;
            } catch {
              return result.body;
            }
          })(),
        },
      };
      if (ok) console.log(`[ApiConsole] ${req.method} /${req.url}`, log);
      else console.error(`[ApiConsole] ${req.method} /${req.url}`, log);
      setRequests((prev) =>
        prev.map((r) => (r.id === req.id ? { ...r, result: { ...result, ok, hasFile: !!file } } : r))
      );
    } catch (e) {
      console.error(`[ApiConsole] ${req.method} /${req.url}`, e);
      const time = Math.round(performance.now() - start);
      const body =
        e instanceof TypeError
          ? `${String(e)}\n\nNo response reached the browser (status 0). Likely a gateway timeout (Cloud Run 504 — the request exceeded the service timeout) or a network/CORS failure; the backend may have completed. Elapsed: ${time}ms.`
          : String(e);
      setRequests((prev) =>
        prev.map((r) => (r.id === req.id ? { ...r, result: { status: 0, body, time, ok: false, hasFile: !!file } } : r))
      );
    } finally {
      setLoadingIds((prev) => {
        const n = new Set(prev);
        n.delete(req.id);
        return n;
      });
    }
  };

  const pendingTargets = requests.filter((r) => !r.result && !hasUnreplacedParams(r.url));

  const runAll = async () => {
    const targets = requests.filter((r) => !r.result && !hasUnreplacedParams(r.url));
    setRunProgress({ done: 0, total: targets.length });
    try {
      for (const req of targets) {
        await runRequest(req);
        setRunProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
      }
    } finally {
      setRunProgress(null);
    }
  };

  const duplicateRequest = (req: ApiRequest) =>
    setRequests((prev) => {
      const idx = prev.findIndex((r) => r.id === req.id);
      const clone = { ...req, id: nextId(), result: undefined, label: undefined };
      const next = [...prev];
      next.splice(idx, 0, clone);
      return next;
    });

  const clearResult = (id: number) =>
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, result: undefined } : r)));

  const copyShareUrl = () => {
    const payload = requests.map(({ url, method, body, label }) => {
      const base = { url, method, label };
      if (method === 'GET') return base;
      try {
        return { ...base, body: JSON.parse(body) as unknown };
      } catch {
        return { ...base, body };
      }
    });
    const shareUrl = `${window.location.origin}/urls-form?requests=${encodeURIComponent(JSON.stringify(payload))}`;
    void navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setRequests([makeEmptyRequest()]);
    setFiles(new Map());
    window.history.replaceState(null, '', window.location.pathname);
  };

  const updateRequest = (id: number, patch: Partial<Omit<ApiRequest, 'id'>>) =>
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeRequest = (id: number) => {
    setRequests((prev) => prev.filter((r) => r.id !== id));
    setFiles((prev) => {
      const n = new Map(prev);
      n.delete(id);
      return n;
    });
  };

  const addRouteToRequests = (routeInfo: RouteInfo) => {
    const body = routeInfo.example ? JSON.stringify(routeInfo.example, null, 2) : '{}';
    const newData = { url: routeInfo.route.replace(/^\//, ''), method: routeInfo.method, body };
    setRequests((prev) => {
      if (prev.length === 1 && isDefaultRequest(prev[0])) return [{ ...prev[0], ...newData }];
      return [...prev, { id: nextId(), ...newData }];
    });
  };

  const filteredRoutes = allRoutes.filter((routeInfo) => {
    if (methodFilter && routeInfo.method !== methodFilter) return false;
    const routeStr = `${routeInfo.method} ${routeInfo.route}`;
    if (tagFilter && !routeStr.includes(tagFilter)) return false;
    const search = routeSearch.toLowerCase().trim();
    if (!search) return true;
    return search.split(/\s+/).every((w) => routeStr.toLowerCase().includes(w));
  });

  return (
    <Box
      sx={{
        p: 3,
        display: 'flex',
        gap: 3,
        alignItems: 'flex-start',
        bgcolor: SURFACE,
        minHeight: '100vh',
      }}
    >
      {/* ── Routes panel ── */}
      <Box
        sx={{
          width: 300,
          flexShrink: 0,
          position: 'sticky',
          top: 16,
          bgcolor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ color: NAVY, letterSpacing: '0.04em' }}>
            ENDPOINTS
          </Typography>
          <IconButton size="small" onClick={fetchRoutes} disabled={routesLoading} sx={{ color: NAVY }}>
            {routesLoading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder="Buscar..."
          value={routeSearch}
          autoFocus
          onChange={(e) => setRouteSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS_ITEM,
              bgcolor: alpha(NAVY, 0.03),
              '& fieldset': { borderColor: alpha(NAVY, 0.12) },
            },
          }}
        />

        {/* Method filter chips */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {METHODS.map((m) => (
            <Chip
              key={m}
              label={m}
              size="small"
              variant={methodFilter === m ? 'filled' : 'outlined'}
              onClick={() => setMethodFilter(methodFilter === m ? '' : m)}
              sx={{
                fontWeight: 700,
                fontSize: 10,
                color: METHOD_COLORS[m],
                borderColor: METHOD_COLORS[m],
                height: 20,
                ...(methodFilter === m && { bgcolor: `${METHOD_COLORS[m]}18` }),
              }}
            />
          ))}
        </Box>

        {/* Quick tag chips */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
          {QUICK_TAGS.map((tag) => (
            <Chip
              key={tag.value}
              label={tag.label}
              size="small"
              variant={tagFilter === tag.value ? 'filled' : 'outlined'}
              onClick={() => setTagFilter(tagFilter === tag.value ? '' : tag.value)}
              sx={{
                fontFamily: 'monospace',
                fontSize: 10,
                height: 20,
                ...(tagFilter === tag.value && { bgcolor: alpha(NAVY, 0.08) }),
              }}
            />
          ))}
          <Chip
            label={`${filteredRoutes.length} endpoints`}
            size="small"
            sx={{ fontSize: 10, height: 20, ml: 'auto', bgcolor: alpha(NAVY, 0.04), color: alpha(NAVY, 0.5) }}
          />
        </Box>

        <Box sx={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
          {filteredRoutes.length === 0 && !routesLoading && (
            <Typography variant="body2" color="text.disabled" sx={{ p: 1 }}>
              Sin resultados
            </Typography>
          )}
          {filteredRoutes.map((routeInfo) => (
            <Box
              key={`${routeInfo.method} ${routeInfo.route}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1,
                py: 0.75,
                cursor: 'pointer',
                borderRadius: RADIUS_ITEM,
                '&:hover': { bgcolor: alpha(NAVY, 0.05) },
              }}
              onClick={() => addRouteToRequests(routeInfo)}
            >
              <MethodBadge method={routeInfo.method} />
              <Typography variant="caption" sx={{ wordBreak: 'break-all', color: NAVY, opacity: 0.75, pt: '1px' }}>
                {routeInfo.route}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Editor ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" sx={{ color: NAVY }}>
            URLs Form
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<DeleteIcon />}
              variant="outlined"
              onClick={clearAll}
              disabled={requests.length === 1 && isDefaultRequest(requests[0])}
              sx={{ borderRadius: RADIUS_COIN, borderColor: alpha(NAVY, 0.25), color: NAVY }}
            >
              Limpiar todo
            </Button>
            <Tooltip title={copied ? '¡Copiado!' : 'Copiar URL compartible'}>
              <Button
                startIcon={<CopyIcon />}
                variant="outlined"
                onClick={copyShareUrl}
                sx={{ borderRadius: RADIUS_COIN, borderColor: alpha(NAVY, 0.25), color: NAVY }}
              >
                {copied ? 'Copiado' : 'Compartir'}
              </Button>
            </Tooltip>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setRequests((prev) => [...prev, makeEmptyRequest()])}
              sx={{ borderRadius: RADIUS_COIN, borderColor: alpha(NAVY, 0.25), color: NAVY }}
            >
              Agregar
            </Button>
            <Button
              startIcon={runProgress ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              variant="contained"
              onClick={runAll}
              disabled={runProgress !== null || pendingTargets.length === 0}
              sx={{
                borderRadius: RADIUS_COIN,
                bgcolor: NAVY,
                color: '#fff',
                boxShadow: SHADOW,
                '&:hover': { bgcolor: '#2a2f6e' },
              }}
            >
              {runProgress
                ? `Ejecutando ${runProgress.done}/${runProgress.total}`
                : `${requests.some((r) => r.result) ? 'Ejecutar pendientes' : 'Ejecutar todos'} (${pendingTargets.length})`}
            </Button>
          </Box>
        </Box>

        {/* Token override */}
        <TextField
          size="small"
          fullWidth
          placeholder="Token override (opcional — por defecto usa tu sesión)"
          value={tokenOverride}
          onChange={(e) => setTokenOverride(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ fontSize: 16, color: tokenOverride ? NAVY : 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS_ITEM,
              fontFamily: 'monospace',
              fontSize: 13,
              bgcolor: tokenOverride ? alpha(NAVY, 0.03) : 'transparent',
              '& fieldset': { borderColor: alpha(NAVY, 0.12) },
            },
          }}
        />

        {/* Requests */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {requests.map((req, index) => {
            const isLoading = loadingIds.has(req.id);
            const file = files.get(req.id);
            const bodyInvalid = req.method !== 'GET' && !isJsonValid(req.body);
            const { result } = req;

            return (
              <Box
                key={req.id}
                sx={{
                  bgcolor: result
                    ? result.ok
                      ? 'rgba(83, 183, 33, 0.05)'
                      : 'rgba(231, 35, 59, 0.05)'
                    : SURFACE_CONTAINER_LOWEST,
                  borderRadius: RADIUS_CARD,
                  boxShadow: SHADOW,
                  p: 1.5,
                  display: 'flex',
                  gap: 1.5,
                }}
              >
                {/* Col 1: index + method + url + body + response */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 24 }}>
                      {req.label ?? `#${index + 1}`}
                    </Typography>
                    <Select
                      value={req.method}
                      size="small"
                      onChange={(e) => updateRequest(req.id, { method: e.target.value as Method })}
                      sx={{
                        minWidth: 100,
                        borderRadius: RADIUS_ITEM,
                        color: METHOD_COLORS[req.method],
                        fontWeight: 700,
                        fontSize: 13,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NAVY, 0.12) },
                      }}
                    >
                      {METHODS.map((m) => (
                        <MenuItem key={m} value={m} sx={{ color: METHOD_COLORS[m], fontWeight: 700 }}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                    <TextField
                      size="small"
                      placeholder="URL (ej: admin/leads/123)"
                      value={req.url}
                      onChange={(e) => updateRequest(req.id, { url: e.target.value })}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: RADIUS_ITEM,
                          fontFamily: 'monospace',
                          fontSize: 13,
                          color: NAVY,
                          '& fieldset': { borderColor: alpha(NAVY, 0.12) },
                        },
                      }}
                    />
                    {result && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Chip
                          label={result.status}
                          size="small"
                          sx={{
                            bgcolor: result.ok ? 'rgba(46,125,50,0.1)' : 'rgba(198,40,40,0.1)',
                            color: result.ok ? '#2e7d32' : '#c62828',
                            fontWeight: 700,
                            borderRadius: RADIUS_ITEM,
                          }}
                        />
                        <Typography variant="caption" sx={{ color: alpha(NAVY, 0.4), fontFamily: 'monospace' }}>
                          {result.time}ms
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {req.method !== 'GET' && (
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      label="Body (JSON)"
                      value={req.body}
                      onChange={(e) => updateRequest(req.id, { body: e.target.value })}
                      onBlur={() => {
                        try {
                          updateRequest(req.id, { body: JSON.stringify(JSON.parse(req.body), null, 2) });
                        } catch {
                          /* keep */
                        }
                      }}
                      error={bodyInvalid}
                      helperText={bodyInvalid ? 'JSON inválido' : undefined}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: RADIUS_ITEM,
                          bgcolor: alpha(NAVY, 0.02),
                          '& fieldset': { borderColor: alpha(NAVY, 0.12) },
                        },
                      }}
                      slotProps={{
                        htmlInput: {
                          style: { fontFamily: 'monospace', fontSize: 13, color: NAVY, resize: 'vertical' },
                        },
                        input: {
                          endAdornment: (
                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                              <CopyIconButton text={req.body} title="Copiar body" />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  )}

                  {result && <ResponseBody result={result} />}
                </Box>

                {/* Col 2: actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, minWidth: 130 }}>
                  <Button
                    startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                    variant="contained"
                    size="small"
                    onClick={() => runRequest(req)}
                    disabled={isLoading || hasUnreplacedParams(req.url) || bodyInvalid}
                    sx={{
                      borderRadius: RADIUS_COIN,
                      bgcolor: YELLOW,
                      color: NAVY,
                      fontWeight: 700,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#e6c000', boxShadow: 'none' },
                    }}
                  >
                    {result ? 'Re-ejecutar' : 'Ejecutar'}
                  </Button>

                  {req.logLink && (
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={req.logLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ borderRadius: RADIUS_COIN, borderColor: alpha(NAVY, 0.2), color: NAVY, fontSize: 12 }}
                    >
                      Ver logs
                    </Button>
                  )}

                  {result && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FileCopyIcon />}
                      onClick={() => duplicateRequest(req)}
                      sx={{ borderRadius: RADIUS_COIN, borderColor: alpha(NAVY, 0.2), color: NAVY, fontSize: 12 }}
                    >
                      Duplicar
                    </Button>
                  )}

                  {req.method !== 'GET' && (
                    <>
                      <input
                        type="file"
                        hidden
                        ref={(el) => {
                          if (el) fileInputRefs.current.set(req.id, el);
                          else fileInputRefs.current.delete(req.id);
                        }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          setFiles((prev) => {
                            const n = new Map(prev);
                            if (f) n.set(req.id, f);
                            else n.delete(req.id);
                            return n;
                          });
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRefs.current.get(req.id)?.click()}
                        sx={{ borderRadius: RADIUS_COIN, borderColor: alpha(NAVY, 0.2), color: NAVY, fontSize: 12 }}
                      >
                        {file ? 'Cambiar archivo' : 'Adjuntar archivo'}
                      </Button>
                      {file && (
                        <Fade in>
                          <Chip
                            label={file.name}
                            size="small"
                            onDelete={() => {
                              setFiles((prev) => {
                                const n = new Map(prev);
                                n.delete(req.id);
                                return n;
                              });
                              const input = fileInputRefs.current.get(req.id);
                              if (input) input.value = '';
                            }}
                            sx={{ borderRadius: RADIUS_ITEM, maxWidth: 200 }}
                          />
                        </Fade>
                      )}
                    </>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    {result && (
                      <Tooltip title="Limpiar resultado">
                        <IconButton
                          size="small"
                          onClick={() => clearResult(req.id)}
                          sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => removeRequest(req.id)}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default UrlsForm;
